import { z } from 'zod';
import { fleetEnrolmentStatusEnum } from '../enums';
import { emailSchema, slugSchema, urlSchema, uuidSchema } from './common';

// Fleet — the two-sided programme. A concept curates its own fleet offering (a
// service catalogue garages price against + named partner-fleet customers); a
// garage opts individual locations in and sets a labour rate per service. Every
// payload is declared once here; backend validators and frontend forms import
// the same schema, so API and client types physically cannot drift (commandment 14).

// --- Concept admin: programme settings (singleton per concept) ---
// Status is changed via the lifecycle action below, never this payload.
export const upsertFleetProgrammeSchema = z.object({
  name: z.string().trim().min(2).max(120),
  intro: z.string().trim().max(2000).nullable().optional(),
  enrolmentInstructions: z.string().trim().max(2000).nullable().optional(),
  contactEmail: emailSchema.nullable().optional(),
  currency: z.string().trim().regex(/^[A-Z]{3}$/, 'invalid_currency').default('GBP'),
});
export type UpsertFleetProgrammePayload = z.infer<typeof upsertFleetProgrammeSchema>;

export const fleetProgrammeStatusActionSchema = z.object({ action: z.enum(['activate', 'archive', 'restore']) });
export type FleetProgrammeStatusAction = z.infer<typeof fleetProgrammeStatusActionSchema>;

// --- Concept admin: service catalogue ---
export const createFleetServiceSchema = z.object({
  name: z.string().trim().min(2).max(120),
  description: z.string().trim().max(1000).nullable().optional(),
  suggestedRate: z.number().nonnegative().max(100000).nullable().optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
  active: z.boolean().optional(),
});
export type CreateFleetServicePayload = z.infer<typeof createFleetServiceSchema>;

export const updateFleetServiceSchema = createFleetServiceSchema.partial();
export type UpdateFleetServicePayload = z.infer<typeof updateFleetServiceSchema>;

// --- Concept admin: partner-fleet customers (mirrors partners) ---
export const createFleetCustomerSchema = z.object({
  name: z.string().trim().min(2).max(120),
  slug: slugSchema.optional(),
  description: z.string().trim().max(1000).nullable().optional(),
  logoUrl: urlSchema.nullable().optional(),
  websiteUrl: urlSchema.nullable().optional(),
  fleetSize: z.number().int().min(0).max(10000000).nullable().optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});
export type CreateFleetCustomerPayload = z.infer<typeof createFleetCustomerSchema>;

export const updateFleetCustomerSchema = createFleetCustomerSchema.partial();
export type UpdateFleetCustomerPayload = z.infer<typeof updateFleetCustomerSchema>;

export const fleetCustomerStatusActionSchema = z.object({ action: z.enum(['publish', 'archive', 'restore']) });
export type FleetCustomerStatusAction = z.infer<typeof fleetCustomerStatusActionSchema>;

// --- Member: opt a location in / update its services + labour rates ---
export const fleetServiceRateSchema = z.object({
  fleetServiceId: uuidSchema,
  offered: z.boolean(),
  labourRate: z.number().nonnegative().max(100000).nullable().optional(),
  notes: z.string().trim().max(500).nullable().optional(),
});
export type FleetServiceRateInput = z.infer<typeof fleetServiceRateSchema>;

export const upsertFleetEnrolmentSchema = z.object({
  status: fleetEnrolmentStatusEnum.default('active'),
  notes: z.string().trim().max(1000).nullable().optional(),
  services: z.array(fleetServiceRateSchema).max(100).default([]),
});
export type UpsertFleetEnrolmentPayload = z.infer<typeof upsertFleetEnrolmentSchema>;
